import { useState, useEffect } from "react";
import "./Register.css";
import logo from "./assets/logo.png";
import bg from "./assets/bg.png";
import whatsappIcon from "./assets/whatsapp.png";

function Register({ id }) {

    const [form, setForm] = useState({
        id: id,
        vehicleNumber: "",
        vehicleOwnerName: "",
        vehicleOwnerNumber: "",
        primaryEmergencyContactName: "",
        primaryEmergencyContactNumber: "",
        secondaryEmergencyContactName: "",
        secondaryEmergencyContactNumber: "",
        dearestRelativeContactName: "",
        dearestRelativeContactNumber: ""
    });

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [isContactSupported, setIsContactSupported] = useState(false);
    const [popup, setPopup] = useState(null);

    /* ✅ Detect Android */
    useEffect(() => {
        const isAndroid = /Android/i.test(navigator.userAgent);
        if (isAndroid && "contacts" in navigator && "ContactsManager" in window) {
            setIsContactSupported(true);
        }
    }, []);

    /* 🔥 Normalize Phone */
    const normalizePhone = (val) => {
        if (!val) return "";

        let digits = val.replace(/\D/g, "");

        if (digits.startsWith("91") && digits.length > 10) {
            digits = digits.slice(-10);
        }

        if (digits.startsWith("0") && digits.length > 10) {
            digits = digits.slice(-10);
        }

        return digits.slice(0, 10);
    };

    /* 📇 CONTACT PICKER (ONLY ONE) */
    const pickContact = async (nameKey, numberKey) => {
        try {
            // Prevent multiple selection
            if (form[numberKey]) {
                setPopup({
                    type: "replace",
                    nameKey,
                    numberKey
                });
                return;
            }

            const contacts = await navigator.contacts.select(["name", "tel"], { multiple: false });

            if (contacts.length > 0) {
                const c = contacts[0];

                setForm(prev => ({
                    ...prev,
                    [nameKey]: c.name?.[0] || "",
                    [numberKey]: normalizePhone(c.tel?.[0] || "")
                }));
            }
        } catch (err) {
            console.error(err);
        }
    };

    /* 🔁 Replace Contact */
    const confirmReplace = async () => {
        try {
            const { nameKey, numberKey } = popup;

            const contacts = await navigator.contacts.select(["name", "tel"], { multiple: false });

            if (contacts.length > 0) {
                const c = contacts[0];

                setForm(prev => ({
                    ...prev,
                    [nameKey]: c.name?.[0] || "",
                    [numberKey]: normalizePhone(c.tel?.[0] || "")
                }));
            }

            setPopup(null);
        } catch (err) {
            console.error(err);
        }
    };

    /* 💬 WhatsApp */
    const openWhatsApp = (phone) => {
        const cleaned = normalizePhone(phone);

        if (!form.vehicleOwnerName) {
            setPopup({
                type: "info",
                message: `This is an optional feature to notify your selected emergency contact via WhatsApp.\n\n` +
                    `please add your name to continue.`
            });
            return;
        }

        if (!cleaned || cleaned.length < 10) {
            setPopup({
                type: "info",
                message: `This is an optional feature to notify your selected emergency contact via WhatsApp.\n\n` +
                    `please add contact number to continue.`
            });
            return;
        }

        const message = encodeURIComponent(
            `Hi, I am ${form.vehicleOwnerName}.\n\n` +
            `I have added your contact in Scan4Help as an emergency contact.\n\n` +
            `Scan4Help helps people reach emergency contacts quickly during critical situations.\n\n` +
            `Learn more: https://scan4help.app\n\n` +
            `Thank you 🙏`
        );

        const appUrl = `whatsapp://send?phone=91${cleaned}&text=${message}`;
        const webUrl = `https://wa.me/91${cleaned}?text=${message}`;

        const win = window.open(appUrl, "_blank");

        setTimeout(() => {
            if (!win || win.closed) {
                window.open(webUrl, "_blank");
            }
        }, 1200);
    };

    /* ❗ VALIDATION */
    const validate = () => {
        let e = {};
        const phone = (v) => /^\d{10}$/.test(v);

        const numbers = [
            form.vehicleOwnerNumber,
            form.primaryEmergencyContactNumber,
            form.secondaryEmergencyContactNumber,
            form.dearestRelativeContactNumber
        ];

        const duplicates = numbers.filter((n, i) => n && numbers.indexOf(n) !== i);

        if (duplicates.length > 0) {
            e.duplicate = "Duplicate phone numbers are not allowed";
        }

        const checkName = (val, key) => {
            if (!val) e[key] = "Required";
            else if (val.length > 50) e[key] = "Max 50 characters";
        };

        const checkPhone = (val, key) => {
            if (!val) e[key] = "Required";
            else if (!phone(val)) e[key] = "Must be 10 digits";
        };

        checkName(form.vehicleOwnerName, "vehicleOwnerName");
        checkPhone(form.vehicleOwnerNumber, "vehicleOwnerNumber");

        checkName(form.primaryEmergencyContactName, "primaryEmergencyContactName");
        checkPhone(form.primaryEmergencyContactNumber, "primaryEmergencyContactNumber");

        checkName(form.secondaryEmergencyContactName, "secondaryEmergencyContactName");
        checkPhone(form.secondaryEmergencyContactNumber, "secondaryEmergencyContactNumber");

        checkName(form.dearestRelativeContactName, "dearestRelativeContactName");
        checkPhone(form.dearestRelativeContactNumber, "dearestRelativeContactNumber");

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        await fetch(`${process.env.REACT_APP_UPDATE_URL}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form)
        });

        setSubmitted(true);
    };

    /* 🔥 INPUT */
    const Input = (placeholder, key, isPhone = false, nameKey = null) => (
        <div className="field input-with-icon">

            <input
                placeholder={placeholder}
                value={form[key]}
                onChange={e =>
                    setForm({
                        ...form,
                        [key]: isPhone
                            ? normalizePhone(e.target.value)
                            : e.target.value
                    })
                }
            />

            {/* 📇 Contact */}
            {isPhone && isContactSupported && (
                <span
                    className="contact-icon"
                    onClick={() => pickContact(nameKey, key)}
                >
                    👤
                </span>
            )}

            {/* 💬 WhatsApp */}
            {isPhone && (
                <span
                    className="whatsapp-icon"
                    onClick={() => openWhatsApp(form[key])}
                >
                    <img src={whatsappIcon} alt="whatsapp" className="whatsapp-icon-img" />
                </span>
            )}

            {errors[key] && <span className="error">{errors[key]}</span>}
        </div>
    );

    /* SUCCESS PAGE */
    if (submitted) {
        return (
            <div className="page success-page" style={{ backgroundImage: `url(${bg})` }}>
                <div className="success-container">
                    <div className="success-icon">✅</div>
                    <h1>You're Protected</h1>
                    <p className="success-text">
                        Your emergency details have been securely saved.
                    </p>
                    <div className="success-quote">
                        “In the moment when help is needed the most,
                        this small step you took today can make a life-saving difference.”
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page" style={{ backgroundImage: `url(${bg})` }}>

            <div className="hero">
                <img src={logo} className="logo" alt="logo" />
                <h1>Scan4Help</h1>
                <p>One small step today can protect a life tomorrow.</p>
            </div>

            <div className="form-wrapper">

                {errors.duplicate && (
                    <div className="error global-error">{errors.duplicate}</div>
                )}

                <div className="section-card">
                    <div className="section-title">🚗 Owner</div>
                    {Input("Owner Name", "vehicleOwnerName")}
                    {Input("Vehicle Number", "vehicleNumber")}
                    {Input("Mobile Number", "vehicleOwnerNumber", true, "vehicleOwnerName")}
                </div>

                <div className="section-card">
                    <div className="section-title">📞 Primary</div>
                    {Input("Name", "primaryEmergencyContactName")}
                    {Input("Mobile", "primaryEmergencyContactNumber", true, "primaryEmergencyContactName")}
                </div>

                <div className="section-card">
                    <div className="section-title">📞 Secondary</div>
                    {Input("Name", "secondaryEmergencyContactName")}
                    {Input("Mobile", "secondaryEmergencyContactNumber", true, "secondaryEmergencyContactName")}
                </div>

                <div className="section-card">
                    <div className="section-title">👨‍👩‍👧 Relative / Friend</div>
                    {Input("Name", "dearestRelativeContactName")}
                    {Input("Mobile", "dearestRelativeContactNumber", true, "dearestRelativeContactName")}
                </div>

                <button className="cta" onClick={handleSubmit}>
                    Save & Protect
                </button>

            </div>

            {/* 🔥 POPUPS */}
            {popup?.type === "info" && (
                <div className="popup-overlay">
                    <div className="popup">
                        <p>{popup.message}</p>
                        <button onClick={() => setPopup(null)}>OK</button>
                    </div>
                </div>
            )}

            {popup?.type === "replace" && (
                <div className="popup-overlay">
                    <div className="popup">
                        <p>A contact is already selected. Replace it?</p>
                        <button onClick={() => setPopup(null)}>Cancel</button>
                        <button onClick={confirmReplace}>Replace</button>
                    </div>
                </div>
            )}

        </div>
    );
}

export default Register;